import { useMemo, useState, useEffect } from "react";
import { Settings, AlertCircle, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Pricing configuration panel.
 * - One Selling % input per unique category
 * - Cost: auto-detected from manifest's liquidation rate.
 *   If any item in the category has missing cost, a Cost % override input is enabled.
 */
const PricingConfigPanel = ({ result, onPricingUpdated }) => {
  // Build per-category state from result items
  const categories = useMemo(() => {
    const map = new Map();
    (result.items || []).forEach((item) => {
      const cat = item.category_description || "Uncategorized";
      if (!map.has(cat)) {
        map.set(cat, {
          category: cat,
          itemCount: 0,
          missingCostCount: 0,
          autoCostSum: 0,
          autoCostN: 0,
          defaultSellingPct: item.selling_pct || 0,
          defaultCostPct: item.cost_pct || 0,
        });
      }
      const entry = map.get(cat);
      entry.itemCount += 1;
      if (!item.cost_detected) {
        entry.missingCostCount += 1;
      } else {
        entry.autoCostSum += item.liquidation_rate || 0;
        entry.autoCostN += 1;
      }
      // Track defaults as the most-common selling pct (use first seen as default)
      if (!entry.defaultSellingPct) entry.defaultSellingPct = item.selling_pct || 0;
      if (!entry.defaultCostPct) entry.defaultCostPct = item.cost_pct || 0;
    });
    return Array.from(map.values()).sort((a, b) => b.itemCount - a.itemCount);
  }, [result.items]);

  const [pricing, setPricing] = useState({});
  const [isApplying, setIsApplying] = useState(false);

  // Initialize pricing state when categories change
  useEffect(() => {
    const initial = {};
    categories.forEach((c) => {
      initial[c.category] = {
        selling_pct: c.defaultSellingPct?.toFixed?.(2) ?? String(c.defaultSellingPct || ""),
        overrideCost: c.missingCostCount > 0, // auto-enable override when items are missing cost
        cost_pct: c.defaultCostPct?.toFixed?.(2) ?? String(c.defaultCostPct || ""),
      };
    });
    setPricing(initial);
  }, [categories]);

  const handleChange = (category, field, value) => {
    setPricing((prev) => ({
      ...prev,
      [category]: { ...prev[category], [field]: value },
    }));
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const payload = {
        pricing: categories.map((c) => {
          const p = pricing[c.category] || {};
          const sellingPct = parseFloat(p.selling_pct);
          const item = {
            category: c.category,
            selling_pct: isNaN(sellingPct) ? 0 : sellingPct,
          };
          if (p.overrideCost) {
            const cp = parseFloat(p.cost_pct);
            if (!isNaN(cp)) item.cost_pct_override = cp;
          }
          return item;
        }),
      };
      const response = await axios.post(
        `${API}/recalculate-pricing/${result.id}`,
        payload
      );
      toast.success("Pricing updated successfully");
      onPricingUpdated(response.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to apply pricing");
    } finally {
      setIsApplying(false);
    }
  };

  if (!categories.length) return null;

  const hasMissingCost = categories.some((c) => c.missingCostCount > 0);

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-6 space-y-4"
      data-testid="pricing-config-panel"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold heading-font text-slate-900 flex items-center">
          <Settings className="w-5 h-5 mr-2" />
          Pricing Configuration
        </h3>
        <button
          onClick={handleApply}
          disabled={isApplying}
          className="refinery-button flex items-center"
          data-testid="apply-pricing-button"
        >
          {isApplying ? (
            <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          {isApplying ? "Applying..." : "Apply Pricing"}
        </button>
      </div>

      {hasMissingCost && (
        <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-sm p-3 mono-font text-sm text-amber-900">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            Some items have no liquidation rate detected. Toggle{" "}
            <span className="font-bold">Override Cost</span> for the affected
            categories and enter a Cost %.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="data-table w-full" data-testid="pricing-config-table">
          <thead>
            <tr>
              <th>Category</th>
              <th className="text-center">Items</th>
              <th className="text-center">Auto Cost</th>
              <th className="text-center">Selling %</th>
              <th className="text-center">Override Cost</th>
              <th className="text-center">Cost %</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => {
              const p = pricing[c.category] || {};
              const avgAutoCost =
                c.autoCostN > 0
                  ? (c.autoCostSum / c.autoCostN).toFixed(2)
                  : null;
              return (
                <tr key={c.category} data-testid={`pricing-row-${c.category}`}>
                  <td className="font-medium">{c.category}</td>
                  <td className="text-center">
                    {c.itemCount}
                    {c.missingCostCount > 0 && (
                      <span className="ml-1 text-xs text-amber-700">
                        ({c.missingCostCount} no cost)
                      </span>
                    )}
                  </td>
                  <td className="text-center mono-font text-xs">
                    {avgAutoCost ? `${avgAutoCost}%` : "—"}
                  </td>
                  <td className="text-center">
                    <div className="inline-flex items-center">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={p.selling_pct ?? ""}
                        onChange={(e) =>
                          handleChange(c.category, "selling_pct", e.target.value)
                        }
                        className="w-20 px-2 py-1 border border-slate-300 rounded text-right mono-font text-sm"
                        data-testid={`selling-pct-input-${c.category}`}
                      />
                      <span className="ml-1 text-slate-500">%</span>
                    </div>
                  </td>
                  <td className="text-center">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!p.overrideCost}
                        onChange={(e) =>
                          handleChange(
                            c.category,
                            "overrideCost",
                            e.target.checked
                          )
                        }
                        className="form-checkbox h-4 w-4 text-emerald-600"
                        data-testid={`override-cost-toggle-${c.category}`}
                      />
                    </label>
                  </td>
                  <td className="text-center">
                    <div className="inline-flex items-center">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={p.cost_pct ?? ""}
                        disabled={!p.overrideCost}
                        onChange={(e) =>
                          handleChange(c.category, "cost_pct", e.target.value)
                        }
                        className="w-20 px-2 py-1 border border-slate-300 rounded text-right mono-font text-sm disabled:bg-slate-100 disabled:text-slate-400"
                        data-testid={`cost-pct-input-${c.category}`}
                      />
                      <span className="ml-1 text-slate-500">%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 mono-font">
        Cost is auto-detected from each item's liquidation rate in the manifest.
        Use <span className="font-bold">Override Cost</span> to apply a uniform
        Cost % to all items in that category (useful when liquidation rate is
        missing).
      </p>
    </div>
  );
};

export default PricingConfigPanel;
